// components/ManageUsers/ManageUsersBody.tsx
import React, { useEffect, useState } from 'react';
import { Btn, H6, P } from '../../AbstractElements';
import { useFetchUsers } from '../../hooks/manageUsers/useFetchUsers';
import { format } from 'date-fns';
import { Table, Input, Row, Col, Card, CardBody, Container, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { Trash, Edit } from 'react-feather';
import { useNavigate } from 'react-router-dom';
import { useSoftDeleteUser } from '../../hooks/manageUsers/useSoftDeleteUser';
import CommonCardHeader from '../../Utils/CommonComponents/CommonCardHeader';
import Breadcrumbs from '../../CommonElements/Breadcrumbs';

function formatDate(dt?: string | Date | null) {
    if (!dt) return '—';
    try {
        const d = typeof dt === 'string' ? new Date(dt) : dt;
        return format(d, 'yyyy-MM-dd HH:mm');
    } catch {
        return String(dt);
    }
}

export default function ManageUsersBody() {
    const { users, loading, error, page, setPage, role, setRole, search, setSearch, refetch, totalRecords, limit } = useFetchUsers();
    const { handleSoftDelete, loading: deleteLoading } = useSoftDeleteUser();
    const rows = users ?? [];
    const navigate = useNavigate();

    // LOCAL input state for debouncing
    const [localSearch, setLocalSearch] = useState<string>(search ?? '');

    // State for delete confirmation modal
    const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
    const [selectedUserToDelete, setSelectedUserToDelete] = useState<any | null>(null);

    // Sync localSearch when external search changes (e.g., initial load or programmatic change)
    useEffect(() => {
        setLocalSearch(search ?? '');
    }, [search]);

    // Debounce: update the hook's search only after user has stopped typing
    useEffect(() => {
        const handler = setTimeout(() => {
            // Reset page to 1 when search changes
            setPage && setPage(1);
            setSearch(localSearch);
        }, 500); // 500ms debounce - adjust as needed

        return () => clearTimeout(handler);
        // intentionally include setPage and setSearch to keep lint happy
    }, [localSearch, setSearch, setPage]);

    // Keep the refetch on mount (as you had)
    useEffect(() => {
        refetch();
    }, [refetch]);

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setLocalSearch(e.target.value);
    };

    const isBusy = loading || deleteLoading;

    // open confirmation modal
    const confirmDelete = (user: any) => {
        setSelectedUserToDelete(user);
        setDeleteModalOpen(true);
    };

    // cancel deletion
    const cancelDelete = () => {
        setSelectedUserToDelete(null);
        setDeleteModalOpen(false);
    };

    // perform deletion
    const performDelete = async () => {
        if (!selectedUserToDelete) return;
        try {
            await handleSoftDelete(selectedUserToDelete._id);
            // close modal and refetch
            setDeleteModalOpen(false);
            setSelectedUserToDelete(null);
            refetch();
        } catch (err) {
            // if hooks throw, keep modal open so user can retry or cancel
            // you may want to show toast inside the hook; we don't change that here
            console.error('Delete failed', err);
        }
    };

    return (
        <>
            <Breadcrumbs pageTitle={"Manage Users"} parent={""} title={"Manage Users"} paragraph='"Manage the Users from here"' />
            <Container className='default-dashboard' fluid>
                <Row>
                    <Col xxl={12} xl={12} lg={12} md={12} className="box-col-7">
                        <Card>
                            <CommonCardHeader title={"Users"} headClass="card-no-border pb-0" />
                            <CardBody className="pt-0">

 <div className="mb-3">
                                    <div className="alert d-flex align-items-start" role="alert" style={{ borderRadius: 8 }}>
                                        <div style={{ marginRight: 12, fontSize: 20, lineHeight: 1, color: '#FF3500' }}>
                                            {/* simple info icon (i) */}
                                            <svg width="20" height="20" viewBox="0 0 16 16" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden>
                                                <path d="M8 1a7 7 0 1 0 0 14A7 7 0 0 0 8 1zm0 12.93A5.93 5.93 0 1 1 8 2.07a5.93 5.93 0 0 1 0 11.86z"/>
                                                <path d="M8.93 6.588a.5.5 0 0 0-.858-.514L7.5 7.42 7.042 6.074a.5.5 0 0 0-.884.468l.8 2a.5.5 0 0 0 .94.008l.99-2.382zM8 11a.9.9 0 1 0 0-1.8A.9.9 0 0 0 8 11z"/>
                                            </svg>
                                        </div>
                                        <div>
                                            <strong style={{ color: '#FF3500' }}>Note:</strong>
                                            <P className="mb-0" style={{ color: '#FF3500' }}>
                                                To add participants — you must create a new group, or revisit the group members via the <strong>View Participants</strong> button.
                                            </P>
                                        </div>
                                    </div>
                                </div>
                                <div className="mb-3">
                                    <Row>
                                        <Col>
                                            <div className="d-flex justify-content-end align-items-center">
                                                <div className="position-relative m-2" style={{ width: '260px' }}>
                                                    <Input
                                                        type="text"
                                                        placeholder="Search by name or email"
                                                        value={localSearch}
                                                        onChange={handleSearchChange}
                                                        className="form-control"
                                                        style={{
                                                            borderRadius: '25px',
                                                            paddingLeft: '40px',
                                                            paddingRight: '15px',
                                                            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                                            border: '1px solid #ddd',
                                                            transition: 'all 0.2s ease',
                                                        }}
                                                        onFocus={(e) => (e.target.style.boxShadow = '0 0 0 3px rgba(0, 123, 255, 0.25)')}
                                                        onBlur={(e) => (e.target.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)')}
                                                    />
                                                    <i
                                                        className="fa fa-search"
                                                        style={{
                                                            position: 'absolute',
                                                            left: '14px',
                                                            top: '50%',
                                                            transform: 'translateY(-50%)',
                                                            color: '#888',
                                                        }}
                                                    ></i>
                                                </div>
                                            </div>
                                        </Col>
                                    </Row>
                                </div>

                                <div className="table-responsive theme-scrollbar">
                                    <Table className="display table-bordernone dataTable no-footer">
                                        <thead>
                                            <tr role="row">
                                                <th>User</th>
                                                <th>Email</th>
                                                <th>User Role</th>
                                                <th>Created At</th>
                                                <th>Invite Accepted</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>

                                        <tbody>
                                            {isBusy ? (
                                                <tr>
                                                    <td colSpan={6}>Loading...</td>
                                                </tr>
                                            ) : error ? (
                                                <tr>
                                                    <td colSpan={6} style={{ color: 'red' }}>{error}</td>
                                                </tr>
                                            ) : rows.length === 0 ? (
                                                <tr>
                                                    <td colSpan={6}>No users found.</td>
                                                </tr>
                                            ) : (
                                                rows.map(user => (
                                                    <tr key={user._id}>
                                                        <td>
                                                            <div>
                                                                <H6 className="mb-0">{user.firstname} {user.lastname}</H6>
                                                                <P className="mb-0">{user.email}</P>
                                                            </div>
                                                        </td>

                                                        <td>{user.email}</td>

                                                        <td>{user.role?.name ?? '—'}</td>

                                                        <td>{formatDate(user.createdAt)}</td>

                                                        <td>{(user.accepted != null) ? (user.accepted ? 'Yes' : 'No') : 'Status not found'}</td>

                                                        <td>
                                                            <div style={{ gap: 8, display: 'inline-flex', alignItems: 'center' }}>
                                                                <Btn
                                                                    color="outline"
                                                                    onClick={() => navigate(`${process.env.PUBLIC_URL}/manage_users/edit`, { state: { user: user } })}
                                                                >
                                                                    <Edit />
                                                                </Btn>
                                                                <Btn
                                                                    color="outline"
                                                                    onClick={() => confirmDelete(user)}
                                                                    disabled={isBusy}
                                                                    title="Delete user"
                                                                >
                                                                    <Trash />
                                                                </Btn>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>

                                        <tfoot>
                                            <tr>
                                                <td colSpan={6}>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16 }}>
                                                        <Btn color="outline" onClick={() => setPage(Math.max(1, page - 1))} disabled={page <= 1}>Previous</Btn>
                                                        <Btn color="outline" disabled={(totalRecords / limit) <= page} onClick={() => setPage(page + 1)}>Next</Btn>
                                                    </div>
                                                </td>
                                            </tr>
                                        </tfoot>
                                    </Table>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>

            {/* Delete confirmation modal - uses existing reactstrap Modal and the same button components */}
            <Modal
                isOpen={deleteModalOpen}
                toggle={cancelDelete}
                centered
                className="modal-dialog-centered"
            >
                <ModalHeader toggle={cancelDelete} className="bg-light text-dark">
                    Confirm Deletion
                </ModalHeader>
                <ModalBody>
                    <div className="py-2">
                        <H6 className="mb-2 text-dark">Are you sure you want to delete this user?</H6>
                        <P className="mb-1 text-dark">
                            {selectedUserToDelete
                                ? `${selectedUserToDelete.firstname ?? ''} ${selectedUserToDelete.lastname ?? ''} (${selectedUserToDelete.email ?? '—'})`
                                : 'Selected user not available.'}
                        </P>
                        {/* <P className="text-muted small mb-0">This action will perform a soft delete (user data will be retained but marked as deleted).</P> */}
                    </div>
                </ModalBody>
                <ModalFooter>
                    <div className="d-flex" style={{ gap: 8 }}>
                        <Btn color="light text-dark" onClick={cancelDelete} disabled={deleteLoading}>Cancel</Btn>
                        <Btn color="danger" onClick={performDelete} disabled={deleteLoading}>
                            {deleteLoading ? 'Deleting...' : 'Yes, Delete'}
                        </Btn>
                    </div>
                </ModalFooter>
            </Modal>
        </>
    );
}
